// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-imports-ts
import { Component, Prop, Watch, State, h, Env, Host, Listen } from '@stencil/core';

import { DeviceType } from '../../../../types/base/affiliate/characteristics';
import { AffiliateControllerResponse } from '../../../../types/controllers/affiliate';
import { FormattedPromotion } from '../../types';
import { formatPromotions } from '../../utils';
import { ageClass } from '../../utils/age-check';
import { getPlacement } from '../../utils/api';
import { filterPromotions } from '../../utils/filter';
import { DEFAULT_ROTATE_TIME } from '../../utils/image';

@Component({
    tag: 'bam-sticky-cta',
    styleUrl: 'bam-sticky-cta.css',
    shadow: true,
})
export class BamStickyCta {
    // props
    /** (required) */
    @Prop() placementId: number;
    /** (required) */
    @Prop() propertyId: number;
    /** (required) */
    @Prop() context: string;

    // optional characteristics props
    @Prop() location: string;
    @Prop() pageSlug: string;
    @Prop() subscriptionTier: 0 | 1 | 2 | 3;
    @Prop() wasPro: boolean;
    @Prop() subdivision: number;
    @Prop() league: string;
    @Prop() gameId: number;
    @Prop() ctaType: string;
    @Prop() isMobile: boolean;
    @Prop() deviceType: DeviceType;
    @Prop() isBetsyncUser: boolean;
    @Prop() userParentBookIds: string;
    @Prop() isLoggedIn: boolean;
    @Prop() sponsorSlug: string;
    @Prop() affiliate: string;
    @Prop() affiliateType: string;
    @Prop() internalId: string;

    // optional props for functionality
    @Prop() defer: boolean;
    @Prop() devMode = false;
    @Prop() rotateTime = DEFAULT_ROTATE_TIME;
    @Prop() rotate = true;
    @Prop() age: number;
    @Prop() hideUnderage = false;
    @Prop() darkMode = false;
    @Prop() terms: 'geo' | 'affiliate' = 'geo';
    /** When set to 'true', user must scroll 5% of the page before this appears, fades in */
    @Prop() showOnScroll = false;

    // state
    @State() promotions: Array<FormattedPromotion> = [];
    @State() hasScrolled = false;

    @Listen('scroll', { target: 'window' })
    handleScroll() {
        const percentToShow = 0.05;
        if (!this.hasScrolled && window.scrollY > window.innerHeight * percentToShow) {
            this.hasScrolled = true;
        }
    }

    @Watch('defer')
    onDeferChange(newDeferValue) {
        if (!newDeferValue) {
            getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.location,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    user_parent_book_ids: this.userParentBookIds ? this.userParentBookIds.split(',').map(Number) : [],
                    is_logged_in: this.isLoggedIn,
                    sponsor_slug: this.sponsorSlug,
                    context: this.context,
                },
                this.setNewPromosToState.bind(this),
                this.devMode,
                'Sticky CTA'
            );
        }
    }

    connectedCallback() {
        if (!this.defer && Env.BAM_HYDRATE !== 'true') {
            getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.location,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    user_parent_book_ids: this.userParentBookIds ? this.userParentBookIds.split(',').map(Number) : [],
                    is_logged_in: this.isLoggedIn,
                    sponsor_slug: this.sponsorSlug,
                    context: this.context,
                },
                this.setNewPromosToState.bind(this),
                this.devMode,
                'Sticky CTA'
            );
        }
    }

    async componentWillLoad() {
        if (Env.BAM_HYDRATE === 'true') {
            await getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.location,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    user_parent_book_ids: this.userParentBookIds ? this.userParentBookIds.split(',').map(Number) : [],
                    is_logged_in: this.isLoggedIn,
                    sponsor_slug: this.sponsorSlug,
                    context: this.context,
                },
                this.setNewPromosToState.bind(this),
                this.devMode,
                'Sticky CTA'
            );
        }
    }

    setNewPromosToState(placement: AffiliateControllerResponse, location: string) {
        const promolist = formatPromotions(placement.promotions, {
            allowAltLink: placement.allow_alternate_link,
            promotionListId: placement.id,
            location: location,
            context: this.context,
            propertyId: this.propertyId,
        });

        const promotions = filterPromotions(
            {
                internal_identifier: this.internalId,
                affiliate_name: this.affiliate,
                affiliate_type: this.affiliateType,
            },
            promolist
        );

        if (!promotions.length) {
            // eslint-disable-next-line no-console
            console.warn('BAM Promotion Sticky CTA: No active or valid promotions returned');
            this.promotions = [];
            return;
        }
        this.promotions = promotions;
    }

    render() {
        let hideCSS = '';
        if (this.showOnScroll) {
            hideCSS = 'hide-before-scroll';
            if (this.hasScrolled) {
                hideCSS = 'show-after-scroll';
            }
        }

        return !this.defer && this.promotions.length ? (
            this.rotate && this.promotions.length > 1 ? (
                <Host class={ageClass(this.age, this.promotions, this.hideUnderage)}>
                    <div class={hideCSS}>
                        <rotating-sticky-cta
                            placement-id={this.placementId}
                            property-id={this.propertyId}
                            context={this.context}
                            promotions={this.promotions}
                            location={this.location}
                            league={this.league}
                            page-slug={this.pageSlug}
                            rotateTime={this.rotateTime}
                            dark-mode={this.darkMode}
                            terms={this.terms}
                        ></rotating-sticky-cta>
                    </div>
                </Host>
            ) : (
                <Host class={ageClass(this.age, this.promotions, this.hideUnderage)}>
                    <div class={hideCSS}>
                        <static-sticky-cta
                            placement-id={this.placementId}
                            property-id={this.propertyId}
                            context={this.context}
                            promotion={this.promotions[0]}
                            location={this.location}
                            league={this.league}
                            page-slug={this.pageSlug}
                            dark-mode={this.darkMode}
                            terms={this.terms}
                        ></static-sticky-cta>
                    </div>
                </Host>
            )
        ) : null;
    }
}
